import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider, FormControlLabel, Radio, RadioGroup, IconButton, Icon, Collapse, Checkbox } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    box: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 8,
        borderColor: '#cfcfcf',
        paddingLeft: 15,
        paddingRight: 15,
        marginBottom: 24,
        backgroundColor: '#fff'
    },
    text: {
        fontSize: '1.4em',
        color: '#525f7f',
        fontWeight: 'bold'
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 20,
        marginBottom: 20
    },
    editBtn: {
        marginLeft: 24,
        padding: '.4rem .65rem',
        ...themeColors.greenBtn
    },
    tableContainer: {
        width: '100%',
        marginBottom: 24
    }
}));

const PolicyDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const { id } = useParams();
    const classes = useStyles();
    const history = useHistory();
    const [showList, setShowList] = useState(false);
    const [state, setState] = useState({
        loading: true,
        policy: {
            name: '',
            description: '',
            allow: true
        },
        actions: [],
        submodules: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Policy detail | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/iamPolicy/${id}`);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                policy: response.data,
                actions: response.actions,
                submodules: response.submodules,
            }));
        } else {
            console.log('[PolicyDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const toggleSubmodule = name => {
        const submodules = state.submodules.slice();

        submodules.map(submodule => {
            if (submodule.name === name) {
                submodule.open = !submodule.open;
            }

            return submodule;
        });

        setState(prev => ({ ...prev, submodules }));
    }

    const getModule = ({ row }) => row?.submodule?.module?.name ?? '?';
    const getSubmodule = ({ row }) => row?.submodule?.name ?? '?';

    const columns = [
        { field: 'module', headerName: 'Module', flex: 1, minWidth: 200, valueGetter: getModule },
        { field: 'submodule', headerName: 'Submodule', flex: 1, minWidth: 200, valueGetter: getSubmodule },
        { field: 'name', headerName: 'Action name', flex: 1, minWidth: 200 },
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Detail'}</h2>

            <Grid container>
                <Grid item xs={12} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Name'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Name'
                                name='name'
                                value={state.policy.name}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Effect'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <RadioGroup row value={state.policy.allow}>
                                <FormControlLabel value={true} control={<Radio color='primary' />} label='Allow' />
                                <FormControlLabel value={false} control={<Radio color='primary' />} label='Deny' />
                            </RadioGroup>
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} lg={9}>
                    <FormGroup>
                        <FormLabel>{'Description'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Description'
                                name='description'
                                value={state.policy.description}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <div className={classes.row}>
                <h2>{'Permissions'}</h2>

                <div>
                    <FormControlLabel
                        label='List'
                        control={<Checkbox color='primary' checked={showList} onChange={() => setShowList(!showList)} />}
                    />
                    {
                        !state.policy?.default &&
                        <Button
                            variant='contained'
                            className={classes.editBtn}
                            onClick={() => history.push(`/iam/policies/detail/${id}/edit`)}

                        >
                            {'Edit permissions'}
                        </Button>
                    }
                </div>
            </div>

            {
                showList ?
                    <div className={classes.tableContainer}>
                        <DataGrid
                            rows={state.actions}
                            columns={columns}
                            pageSize={20}
                            density='compact'
                            autoHeight
                            getRowId={row => row.idiam_submodule_action}
                        />
                    </div>
                    :
                    state.submodules.map(submodule =>
                        <div key={submodule?.name} className={classes.box}>
                            <div className={classes.row}>
                                <span className={classes.text}>{submodule?.name} {`(${submodule?.actions?.length})`}</span>
                                <IconButton onClick={() => toggleSubmodule(submodule.name)}>
                                    <Icon>{submodule?.open ? 'expand_less' : 'expand_more'}</Icon>
                                </IconButton>
                            </div>

                            <Collapse in={submodule?.open}>
                                <Grid container style={{ paddingBottom: 15 }}>
                                    {
                                        submodule?.actions?.map(action =>
                                            <Grid item key={action?.idiam_submodule_action} xs={6} lg={4}>
                                                <FormControlLabel
                                                    control={<Checkbox color='primary' checked={true} />}
                                                    label={action?.name}
                                                />
                                            </Grid>
                                        )
                                    }
                                </Grid>
                            </Collapse>
                        </div>
                    )
            }

        </div >
    );
}

export default PolicyDetail;